import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/nav/Navbar';
import ArtistHero from '../components/artist/ArtistHero';
import ExclusiveContentCard from '../components/artist/ExclusiveContentCard';
import PatronsList from '../components/artist/PatronsList';
import SubscriptionTiers from '../components/artist/SubscriptionTiers';
import TierBenefitsOverview from '../components/artist/TierBenefitsOverview';
import CrossPromotionSection from '../components/artist/CrossPromotionSection';
import PartnershipSection from '../components/artist/PartnershipSection';
import LatestReleases from '../components/home/LatestReleases';
import LoadingScreen from '../components/common/LoadingScreen';
import ErrorState from '../components/common/ErrorState';
import EmptyState from '../components/common/EmptyState';
import ToastContainer from '../components/notifications/ToastContainer';
import { useArtist } from '../hooks/useArtist';
import { useAccessGrants } from '../hooks/useAccessGrants';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';

const ALL_TABS = ['Vault', 'Releases', 'Membership', 'Network', 'Partnerships', 'Bio'];

const ArtistPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { user } = useAuth();
  const { toasts, addToast, dismissToast } = useToast();

  const { artist, releases, exclusiveContent, patrons, tiers, loading, error, refetch } = useArtist(slug);

  const { hasAccess, hasSubscription, getSubscriptionTier, grantAccess, subscribeTier } = useAccessGrants(
    user?.id,
    {
      onUnlockNotify: (payload) => addToast(payload),
      onSubscribeNotify: (payload) => addToast(payload),
    }
  );

  const [tab, setTab] = useState('Vault');
  const [membershipView, setMembershipView] = useState('tiers');

  if (loading) return <LoadingScreen message="Loading Artist..." />;
  if (error || !artist) return <ErrorState message={error || 'Artist not found'} onRetry={refetch} />;

  const isOwner = user?.id && artist.user_id === user.id;

  const handleUnlock = async ({ contentId, artistId, amount }) => {
    if (!user?.id) return { error: { message: 'Please sign in.' } };
    const content = exclusiveContent.find(c => c.id === contentId);
    return await grantAccess({ userId: user.id, contentId, artistId, amount, contentTitle: content?.title });
  };

  const handleSubscribe = async ({ tierId, amount }) => {
    if (!user?.id) return { error: { message: 'Please sign in.' } };
    const tier = tiers?.find(t => t.id === tierId);
    return await subscribeTier({
      userId: user.id, artistId: artist.id, tierId, amount,
      tierName: tier?.name, artistName: artist.display_name,
    });
  };

  const activeTierId = getSubscriptionTier(artist.id);

  const tabs = ALL_TABS.filter(t => {
    if (t === 'Membership') return tiers?.length > 0;
    return true;
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="pt-20">
        <ArtistHero artist={artist} />

        {/* Tab Nav */}
        <nav className="sticky top-20 bg-zinc-950/95 backdrop-blur-xl border-y border-zinc-900 px-8 h-16 flex items-center gap-8 z-40 overflow-x-auto">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-xs font-black tracking-widest uppercase transition-all h-full px-2 border-b-2 whitespace-nowrap ${
                tab === t ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}>
              {t}
              {t === 'Membership' && tiers?.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded-full">{tiers.length}</span>
              )}
              {t === 'Network' && (
                <span className="ml-2 px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] rounded-full">✦</span>
              )}
              {t === 'Partnerships' && (
                <span className="ml-2 px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 text-[10px] rounded-full">⊕</span>
              )}
            </button>
          ))}
        </nav>

        <main className="max-w-7xl mx-auto py-16 px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Main Column */}
          <div className="lg:col-span-8 space-y-12">

            {tab === 'Vault' && (
              <section className="space-y-8">
                <h2 className="text-4xl font-black tracking-tighter text-white uppercase">Exclusive Content</h2>
                {!user && (
                  <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl flex items-center justify-between gap-4">
                    <p className="text-zinc-300 font-bold text-sm">Sign in to unlock exclusive content.</p>
                    <Link to="/auth" className="px-6 py-3 bg-white text-black font-black uppercase tracking-widest text-xs rounded-full whitespace-nowrap hover:scale-105 transition-all">
                      Sign In
                    </Link>
                  </div>
                )}
                {exclusiveContent.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {exclusiveContent.map(c => (
                      <ExclusiveContentCard key={c.id} content={c}
                        hasAccess={hasAccess(c.id)} onUnlock={handleUnlock} userId={user?.id} />
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No exclusive content yet" subtitle="The artist hasn't published any exclusives yet." />
                )}
              </section>
            )}

            {tab === 'Releases' && (
              <section className="space-y-8">
                <h2 className="text-4xl font-black tracking-tighter text-white uppercase">Discography</h2>
                {releases.length ? (
                  <LatestReleases releases={releases.map(r => ({ ...r, d2f_artists: artist }))} />
                ) : (
                  <EmptyState title="No releases yet" subtitle="Releases will appear here." />
                )}
              </section>
            )}

            {tab === 'Membership' && tiers?.length > 0 && (
              <div className="space-y-10">
                <div className="flex gap-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-1.5 self-start w-fit">
                  {['tiers', 'benefits'].map(v => (
                    <button key={v} onClick={() => setMembershipView(v)}
                      className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                        membershipView === v ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
                      }`}>
                      {v === 'tiers' ? '⊞ Tiers' : '✦ Benefits Overview'}
                    </button>
                  ))}
                </div>
                {membershipView === 'tiers' && (
                  <SubscriptionTiers tiers={tiers} artistId={artist.id} userId={user?.id}
                    hasSubscription={hasSubscription} getSubscriptionTier={getSubscriptionTier} onSubscribe={handleSubscribe} />
                )}
                {membershipView === 'benefits' && (
                  <TierBenefitsOverview tiers={tiers} artistId={artist.id} userId={user?.id}
                    activeTierId={activeTierId} onSubscribe={handleSubscribe} />
                )}
              </div>
            )}

            {tab === 'Network' && (
              <CrossPromotionSection artistId={artist.id} userId={user?.id} isOwner={isOwner} />
            )}

            {tab === 'Partnerships' && (
              <PartnershipSection
                artistId={artist.id}
                userId={user?.id}
                isOwner={isOwner}
                artistName={artist.display_name || artist.name}
                onToast={addToast}
              />
            )}

            {tab === 'Bio' && (
              <section className="space-y-8">
                <h2 className="text-4xl font-black tracking-tighter text-white uppercase">About</h2>
                {artist.statement && (
                  <blockquote className="border-l-4 border-white pl-8 py-2">
                    <p className="text-2xl text-zinc-200 font-bold italic leading-relaxed">"{artist.statement}"</p>
                  </blockquote>
                )}
                {artist.bio
                  ? <p className="text-zinc-300 text-lg leading-relaxed font-bold">{artist.bio}</p>
                  : <EmptyState title="No bio yet" />
                }
                {artist.location && (
                  <p className="text-zinc-500 font-black text-xs uppercase tracking-widest">📍 {artist.location}</p>
                )}
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-10">
            {hasSubscription(artist.id) && (
              <div className="p-6 bg-green-500/10 border border-green-500/30 rounded-2xl space-y-3">
                <p className="text-xs font-black tracking-widest text-green-400 uppercase">Active Member</p>
                <p className="text-white font-black">You're supporting this artist monthly 🎉</p>
                {activeTierId && (
                  <div className="pt-2 border-t border-green-500/20">
                    <p className="text-xs text-green-400/70 font-black uppercase tracking-widest">
                      {tiers.find(t => t.id === activeTierId)?.name} Plan ·{' '}
                      ${tiers.find(t => t.id === activeTierId)?.price}/mo
                    </p>
                  </div>
                )}
                <button onClick={() => { setTab('Membership'); setMembershipView('benefits'); }}
                  className="text-xs font-black uppercase tracking-widest text-green-400 hover:text-green-300 transition-colors">
                  View Your Benefits →
                </button>
              </div>
            )}

            {tiers?.length > 0 && tab !== 'Membership' && !hasSubscription(artist.id) && (
              <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl space-y-5">
                <div className="space-y-1">
                  <p className="text-xs font-black tracking-widest text-zinc-500 uppercase">Support Monthly</p>
                  <p className="text-white font-black text-xl">From ${Math.min(...tiers.map(t => t.price))}/mo</p>
                  <p className="text-zinc-500 text-xs font-bold">{tiers.length} tier{tiers.length > 1 ? 's' : ''} available</p>
                </div>
                <div className="space-y-2">
                  {tiers.slice(0, 3).map(tier => (
                    <div key={tier.id} className="flex items-center gap-2 text-xs text-zinc-400 font-bold">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tier.color || '#3b82f6' }} />
                      {tier.name} · ${tier.price}/mo
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => { setTab('Membership'); setMembershipView('tiers'); }}
                    className="w-full py-4 bg-white text-black font-black uppercase tracking-widest text-xs rounded-2xl hover:scale-105 active:scale-95 transition-all">
                    View Tiers
                  </button>
                  <button onClick={() => { setTab('Membership'); setMembershipView('benefits'); }}
                    className="w-full py-3 border border-zinc-700 text-zinc-400 font-black uppercase tracking-widest text-xs rounded-2xl hover:border-zinc-500 hover:text-zinc-300 transition-all">
                    Compare Benefits
                  </button>
                </div>
              </div>
            )}

            {tab !== 'Partnerships' && (
              <div className="p-6 bg-zinc-900/30 border border-zinc-800/50 rounded-2xl space-y-4">
                <div className="space-y-1">
                  <p className="text-xs font-black tracking-widest text-indigo-400 uppercase">Deals</p>
                  <p className="text-white font-black">Partnerships</p>
                  <p className="text-zinc-500 text-xs font-bold">Revenue shares, co-releases &amp; more</p>
                </div>
                <button onClick={() => setTab('Partnerships')}
                  className="w-full py-3 border border-indigo-500/30 text-indigo-400 font-black uppercase tracking-widest text-xs rounded-2xl hover:border-indigo-500/60 hover:bg-indigo-500/5 transition-all">
                  View Partnerships →
                </button>
              </div>
            )}

            {tab !== 'Network' && (
              <div className="p-6 bg-zinc-900/30 border border-zinc-800/50 rounded-2xl space-y-4">
                <div className="space-y-1">
                  <p className="text-xs font-black tracking-widest text-purple-500 uppercase">Artist Network</p>
                  <p className="text-white font-black">Cross-Promotions</p>
                  <p className="text-zinc-500 text-xs font-bold">See who this artist collaborates with</p>
                </div>
                <button onClick={() => setTab('Network')}
                  className="w-full py-3 border border-purple-500/30 text-purple-400 font-black uppercase tracking-widest text-xs rounded-2xl hover:border-purple-500/60 hover:bg-purple-500/5 transition-all">
                  View Network →
                </button>
              </div>
            )}

            <section className="space-y-6">
              <h3 className="text-xs font-black tracking-widest text-zinc-500 uppercase border-b border-zinc-900 pb-4">
                Top Patrons
              </h3>
              <PatronsList patrons={patrons} />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ArtistPage;